/**
 * Admin Audit Log — the investigation surface over public.audit_logs.
 *
 * audit_logs is populated from two directions:
 *   - DB triggers (migration 20260702150000): every create/update/delete on
 *     business tables plus auth.users lifecycle, regardless of the path that
 *     made the change (app, edge function, GoTrue, SQL editor, FK cascade).
 *     Trigger rows carry metadata.source = 'db_trigger' and metadata.db_role.
 *   - App events written explicitly (impersonate_tenant, manual_override, …).
 *
 * The row dialog shows a field-level old→new diff; delete entries carry the
 * full deleted row in old_values, so a destroyed record can be restored
 * straight from its audit entry (the capability the 2026-07-02 booking-441
 * incident lacked).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, History, User, Calendar, Copy } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

type AuditLog = {
  id: string;
  user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  profiles?: { id: string; name: string | null; email: string | null } | null;
};

const RESOURCE_OPTIONS = [
  "booking",
  "payment",
  "document",
  "workflow",
  "issue",
  "property",
  "property_owner",
  "room",
  "user",
  "profile",
  "protocol",
  "note",
  "role",
];

/**
 * Where the change came from. Trigger rows carry db_role; rows written by app
 * code (impersonate_tenant, manual_override, …) don't and count as "App".
 */
const sourceOf = (
  log: AuditLog,
  t: (key: string, fallback: string) => string
): { label: string; className: string } => {
  if (log.metadata?.source !== "db_trigger") {
    return {
      label: t("admin.audit.source.app", "App"),
      className: "bg-gray-50 text-gray-700 border-gray-200",
    };
  }
  const role = String(log.metadata?.db_role ?? "");
  switch (role) {
    case "authenticated":
      return {
        label: t("admin.audit.source.appUser", "App user"),
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
    case "service_role":
      return {
        label: t("admin.audit.source.backend", "Backend"),
        className: "bg-blue-50 text-blue-700 border-blue-200",
      };
    case "supabase_auth_admin":
      return {
        label: t("admin.audit.source.authGotrue", "Auth (GoTrue)"),
        className: "bg-amber-50 text-amber-700 border-amber-200",
      };
    case "postgres":
      return {
        label: t("admin.audit.source.sqlSystem", "SQL / system"),
        className: "bg-purple-50 text-purple-700 border-purple-200",
      };
    default:
      return {
        label: role || t("admin.audit.source.db", "DB"),
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
  }
};

const ACTION_BADGES: Record<string, string> = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-destructive/10 text-destructive border-destructive/20",
  approve: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reject: "bg-destructive/10 text-destructive border-destructive/20",
  impersonate: "bg-amber-50 text-amber-700 border-amber-200",
};

const actionBadge = (action: string) => {
  const color =
    ACTION_BADGES[action.split("_")[0]] ||
    "bg-gray-50 text-gray-700 border-gray-200";
  return <Badge className={color}>{action}</Badge>;
};

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
};

/** Union of keys across old/new so pure inserts and deletes render too. */
const diffRows = (
  log: AuditLog
): { key: string; from: unknown; to: unknown }[] => {
  const keys = new Set<string>([
    ...Object.keys(log.old_values ?? {}),
    ...Object.keys(log.new_values ?? {}),
  ]);
  return Array.from(keys)
    .sort()
    .map((key) => ({
      key,
      from: log.old_values ? log.old_values[key] : undefined,
      to: log.new_values ? log.new_values[key] : undefined,
    }));
};

export const AdminAuditLogs = () => {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [resourceFilter, setResourceFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("7d");
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const { data: auditLogs, isLoading } = useQuery({
    queryKey: ["admin-audit-logs", resourceFilter, dateFilter],
    queryFn: async () => {
      // `audit_logs.user_id` FKs into auth.users, which PostgREST cannot
      // embed (unexposed schema) — `profiles:user_id (...)` here always threw
      // a PGRST200 relationship error, so this page never loaded a single
      // row. Select the plain columns and hydrate actor profiles separately.
      let query = supabase
        .from("audit_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      if (resourceFilter !== "all") {
        query = query.eq("resource_type", resourceFilter);
      }
      if (dateFilter !== "all") {
        const days = dateFilter === "7d" ? 7 : dateFilter === "30d" ? 30 : 90;
        const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        query = query.gte("created_at", startDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      const rows = (data || []) as AuditLog[];

      const userIds = Array.from(
        new Set(rows.map((r) => r.user_id).filter(Boolean) as string[])
      );
      if (userIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", userIds);
        const byId = new Map(
          (
            (profs ?? []) as Array<{
              id: string;
              name: string | null;
              email: string | null;
            }>
          ).map((p) => [p.id, p])
        );
        for (const r of rows) {
          r.profiles = r.user_id ? (byId.get(r.user_id) ?? null) : null;
        }
      }
      return rows;
    },
  });

  const filteredLogs = useMemo(() => {
    return (auditLogs || []).filter((log) => {
      if (actionFilter !== "all" && log.action.split("_")[0] !== actionFilter) {
        return false;
      }
      if (!search) return true;
      const q = search.toLowerCase();
      const haystacks = [
        log.action,
        log.resource_type,
        log.resource_id,
        log.profiles?.name,
        log.profiles?.email,
        // Search inside changes too — "dimakudenchuk" should find the entries
        // that touched that email even when the actor was the system.
        JSON.stringify(log.old_values ?? ""),
        JSON.stringify(log.new_values ?? ""),
      ];
      return haystacks.some((h) => h?.toLowerCase().includes(q));
    });
  }, [auditLogs, actionFilter, search]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString(language === "de" ? "de-DE" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const copyJson = (log: AuditLog) => {
    const payload = {
      id: log.id,
      created_at: log.created_at,
      action: log.action,
      resource_type: log.resource_type,
      resource_id: log.resource_id,
      actor: log.profiles?.email ?? log.user_id ?? null,
      old_values: log.old_values,
      new_values: log.new_values,
      metadata: log.metadata,
    };
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() =>
        toast.success(
          t("admin.audit.copiedToast", "Audit entry copied as JSON")
        )
      )
      .catch(() =>
        toast.error(
          t("admin.audit.copyFailedToast", "Could not copy to clipboard")
        )
      );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.audit.title", "Audit Log")}</CardTitle>
        <CardDescription>
          {t(
            "admin.audit.description",
            "Every create, update and delete on business data — recorded at the database level, including changes made outside the app. Click a row for the full before/after diff."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t(
                "admin.audit.searchPlaceholder",
                "Search actions, ids, emails, changed values…"
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue
                placeholder={t("admin.audit.filterAllActions", "All Actions")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("admin.audit.filterAllActions", "All Actions")}
              </SelectItem>
              <SelectItem value="create">
                {t("admin.audit.actionCreated", "Created")}
              </SelectItem>
              <SelectItem value="update">
                {t("admin.audit.actionUpdated", "Updated")}
              </SelectItem>
              <SelectItem value="delete">
                {t("admin.audit.actionDeleted", "Deleted")}
              </SelectItem>
              <SelectItem value="impersonate">
                {t("admin.audit.actionImpersonation", "Impersonation")}
              </SelectItem>
              <SelectItem value="manual">
                {t("admin.audit.actionManualOverride", "Manual override")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-[170px]">
              <SelectValue
                placeholder={t(
                  "admin.audit.filterAllResources",
                  "All Resources"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("admin.audit.filterAllResources", "All Resources")}
              </SelectItem>
              {RESOURCE_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue
                placeholder={t("admin.audit.range7d", "Last 7 Days")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">
                {t("admin.audit.range7d", "Last 7 Days")}
              </SelectItem>
              <SelectItem value="30d">
                {t("admin.audit.range30d", "Last 30 Days")}
              </SelectItem>
              <SelectItem value="90d">
                {t("admin.audit.range90d", "Last 90 Days")}
              </SelectItem>
              <SelectItem value="all">
                {t("admin.audit.rangeAll", "All Time")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredLogs.length > 0 ? (
          <>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("admin.audit.colTimestamp", "Timestamp")}
                    </TableHead>
                    <TableHead>{t("admin.audit.colActor", "Actor")}</TableHead>
                    <TableHead>
                      {t("admin.audit.colSource", "Source")}
                    </TableHead>
                    <TableHead>
                      {t("admin.audit.colAction", "Action")}
                    </TableHead>
                    <TableHead>
                      {t("admin.audit.colResource", "Resource")}
                    </TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>
                      {t("admin.audit.colChanges", "Changes")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => {
                    const source = sourceOf(log, t);
                    const changedKeys = Object.keys(
                      log.new_values ?? log.old_values ?? {}
                    );
                    return (
                      <TableRow
                        key={log.id}
                        className="cursor-pointer"
                        onClick={() => setSelected(log)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-sm">
                              {formatDate(log.created_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div>
                              <div className="font-medium">
                                {log.profiles?.name || source.label}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {log.profiles?.email || "—"}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={source.className}>
                            {source.label}
                          </Badge>
                        </TableCell>
                        <TableCell>{actionBadge(log.action)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.resource_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {log.resource_id || "—"}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-muted-foreground max-w-xs truncate">
                            {changedKeys.length
                              ? changedKeys.join(", ")
                              : log.action}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {(auditLogs?.length ?? 0) >= 500 && (
              <p className="text-xs text-muted-foreground mt-2">
                {t(
                  "admin.audit.limitNotice",
                  "Showing the most recent 500 entries — narrow the date or resource filter to see older activity."
                )}
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              {t("admin.audit.empty", "No audit logs found")}
            </p>
          </div>
        )}

        <Dialog
          open={!!selected}
          onOpenChange={(open) => !open && setSelected(null)}
        >
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    {actionBadge(selected.action)}
                    <Badge variant="outline">{selected.resource_type}</Badge>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {selected.resource_id || "—"}
                    </code>
                  </DialogTitle>
                  <DialogDescription>
                    {formatDate(selected.created_at)} ·{" "}
                    {selected.profiles?.name ||
                      selected.profiles?.email ||
                      sourceOf(selected, t).label}{" "}
                    · {t("admin.audit.via", "via")}{" "}
                    {sourceOf(selected, t).label}
                    {selected.metadata?.table
                      ? ` · ${String(selected.metadata.table)}`
                      : ""}
                  </DialogDescription>
                </DialogHeader>

                {diffRows(selected).length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[180px]">
                            {t("admin.audit.diffField", "Field")}
                          </TableHead>
                          <TableHead>
                            {t("admin.audit.diffBefore", "Before")}
                          </TableHead>
                          <TableHead>
                            {t("admin.audit.diffAfter", "After")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {diffRows(selected).map((row) => (
                          <TableRow key={row.key}>
                            <TableCell className="font-mono text-xs align-top">
                              {row.key}
                            </TableCell>
                            <TableCell className="text-xs align-top whitespace-pre-wrap break-all max-w-[240px]">
                              {row.from === undefined ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                formatValue(row.from)
                              )}
                            </TableCell>
                            <TableCell className="text-xs align-top whitespace-pre-wrap break-all max-w-[240px]">
                              {row.to === undefined ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                formatValue(row.to)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "admin.audit.noFieldChanges",
                      "This entry carries no field changes (event-only record)."
                    )}
                  </p>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyJson(selected)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t("admin.audit.copyAsJson", "Copy entry as JSON")}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
